import type { FastifyPluginAsync, FastifyRequest, FastifyReply } from 'fastify'
import { z } from 'zod'
import { db } from '../../core/firebase/admin.js'
import { authMiddleware } from '../../middleware/auth.middleware.js'
import { authService } from './auth.service.js'
import { redisClient } from '../../core/redis/client.js'
import { UsersRepository } from '../users/users.repository.js'
import { UsersService } from '../users/users.service.js'

// Request body schemas
const syncUserSchema = z.object({}).strict()

const updatePreferencesSchema = z.object({
  preferences: z.object({
    locale: z.enum(['fr', 'en']).optional(),
    theme: z.enum(['light', 'dark', 'auto']).optional()
  }).strict()
}).strict()

const usersRepository = new UsersRepository(db)
const usersService = new UsersService(usersRepository, redisClient)

export const authRoutes: FastifyPluginAsync = async (fastify) => {
  /**
   * POST /api/auth/sync
   * Sync Auth0 user to Firestore (create or update)
   * Requires: Authentication middleware
   * Called by frontend after Auth0 authentication
   */
  fastify.post<{ Body: Record<string, unknown> }>('/sync', {
    preHandler: [authMiddleware]
  }, async (request: FastifyRequest<{ Body: Record<string, unknown> }>, reply: FastifyReply) => {
    try {
      // Validate request body (should be empty object)
      syncUserSchema.parse(request.body ?? {})

      if (!request.user) {
        return reply.code(401).send({ error: 'Unauthorized: User not authenticated' })
      }

      const profile = await authService.syncAuth0User(request.user)
      
      return reply.send({
        id: profile.uid,
        email: profile.email,
        displayName: profile.displayName,
        photoURL: profile.photoURL,
        role: profile.customClaims?.admin ? 'admin' : 'user',
        admin: profile.customClaims?.admin || false,
        premium: profile.customClaims?.premium || false,
        subscriptionStatus: profile.customClaims?.subscriptionStatus || 'inactive'
      })
    } catch (error) {
      if (error instanceof z.ZodError) {
        return reply.code(400).send({ error: 'Invalid request body' })
      }
      request.log.error({ err: error }, '[Auth] Failed to sync user')
      return reply.code(500).send({ error: 'Failed to sync user profile' })
    }
  })

  /**
   * GET /api/auth/profile
   * Get user profile from Firestore
   * Requires: Authentication middleware
   */
  fastify.get('/profile', {
    preHandler: [authMiddleware]
  }, async (request: FastifyRequest, reply: FastifyReply) => {
    try {
      if (!request.user) {
        return reply.code(401).send({ error: 'Unauthorized: User not authenticated' })
      }

      const userDoc = await db.collection('users').doc(request.user.uid).get()
      
      if (!userDoc.exists) {
        return reply.code(404).send({ error: 'User not found' })
      }
      
      const userData = userDoc.data()!
      
      return reply.send({
        id: request.user.uid,
        email: request.user.email,
        displayName: userData.displayName || null,
        photoURL: userData.photoURL || null,
        role: userData.role || request.user.role
      })
    } catch (error) {
      request.log.error({ err: error }, '[Auth] Failed to get profile')
      return reply.code(500).send({ error: 'Failed to retrieve profile' })
    }
  })

  
  /**
   * GET /api/auth/me
   * Get FULL current authenticated user data (complete User object with all fields)
   * Includes: gamification, preferences, season, stats, etc.
   * Requires: Authentication middleware
   * This is the endpoint frontend should use to load complete user data
   */
  fastify.get('/me', {
    preHandler: [authMiddleware]
  }, async (request: FastifyRequest, reply: FastifyReply) => {
    try {
      if (!request.user) {
        return reply.code(401).send({ error: 'Unauthorized: User not authenticated' })
      }

      const fullUserProfile = await authService.getUserProfile(request.user.uid)
      return reply.send(fullUserProfile)
    } catch (error) {
      request.log.error({ err: error }, '[Auth] Failed to get full user profile')
      return reply.code(500).send({ error: 'Failed to retrieve user profile' })
    }
  })

  /**
   * GET /api/auth/stats
   * Get user statistics (trips, km, countries)
   * Requires: Authentication middleware
   * NOTE: Use /api/auth/me for complete user data instead
   */
  fastify.get('/stats', {
    preHandler: [authMiddleware]
  }, async (request: FastifyRequest, reply: FastifyReply) => {
    try {
      if (!request.user) {
        return reply.code(401).send({ error: 'Unauthorized: User not authenticated' })
      }
      
      const stats = await usersService.calculateUserStats(request.user.uid)

      return reply.send(stats)
    } catch (error) {
      request.log.error({ err: error }, '[Auth] Failed to get stats')
      return reply.code(500).send({ error: 'Failed to retrieve stats' })
    }
  })

  /**
   * PATCH /api/auth/preferences
   * Update user preferences (locale, theme, etc.)
   * Requires: Authentication middleware
   * Validates: locale (fr, en), theme (light, dark, auto)
   */
  fastify.patch<{ Body: { preferences: { locale?: string; theme?: string } } }>(
    '/preferences',
    {
      preHandler: [authMiddleware]
    },
    async (
      request: FastifyRequest<{
        Body: { preferences: { locale?: string; theme?: string } }
      }>,
      reply: FastifyReply
    ) => {
      try {
        // Validate request body
        const validationResult = updatePreferencesSchema.safeParse(request.body)
        if (!validationResult.success) {
          return reply.code(400).send({ error: 'Invalid request body' })
        }

        if (!request.user) {
          return reply.code(401).send({ error: 'Unauthorized: User not authenticated' })
        }

        const { preferences } = validationResult.data

        // Update user preferences in Firestore
        await db.collection('users').doc(request.user.uid).set(
          {
            preferences: {
              ...preferences,
              updatedAt: new Date().toISOString()
            }
          },
          { merge: true }
        )

        return reply.send({
          success: true,
          preferences
        })
      } catch (error) {
        request.log.error({ err: error }, '[Auth] Failed to update preferences')
        return reply.code(500).send({ error: 'Failed to update preferences' })
      }
    }
  )
}
